import { useContext } from 'react';
import { Input } from 'reactstrap';
import cls from 'clsx';
import { useMutation } from '@tanstack/react-query';
import { useInstanceSelector } from '@s20/hooks/redux.hooks';
import { checkUserHasPermission } from '@s20/selectors/features/permissions.selectors';
import { httpClient } from '@s20/services/httpClient';
import { getStatusName } from '../common';
import { ApiRoutes, ApplicationPermissions } from '../../../../../../Common';
import { JobContext } from '../../JobContext';
import { useUpdateAssignment } from '../../useUpdateAssignment.hook';

export const EquipmentAssignment = ({
    jobAssignmentId, canEdit, ...assignment
}) => {
    const dispatchEditPermission = useInstanceSelector(checkUserHasPermission, ApplicationPermissions.dispatch_edit);
    const { equipment } = useContext(JobContext);

    const mutationHandlers = useUpdateAssignment({
        jobId: assignment.jobId,
        jobAssignmentId,
        onMutate: (equipment, isMatch, resourceId) => {
            if (isMatch) {
                return {
                    ...equipment,
                    assignment: {
                        ...equipment.assignment || {},
                        equipmentAssignedId: resourceId,
                    },
                };
            }

            return equipment;
        },
    });

    const handleEquipmentAssignmentChange = useMutation({
        mutationFn: (resourceId) => httpClient.js(ApiRoutes.dispatch.setEquipmentAssignedEquipment(jobAssignmentId, resourceId)),
        ...mutationHandlers,
    });

    const equipmentAssignedId = assignment?.equipmentAssignedId ?? assignment?.assignedEquipment?.id;

    // does this have required equipment?
    if (
        (
            assignment /* The type of equipment required by the job resource */
                .equipmentType /* The base equipment type */
                .requiredEquipment
            ?? /* Equipment that is required BY THE BASE equipment type....i.e. Arrow Board that requires a Work Truck to get around. */[]
        ).length
    ) {
        /* Do we have any */ // get all of this equipment's required equipment Ids.
        const requiredEquipmentTypeIds = assignment.equipmentType.requiredEquipment.map(
            (x) => x.id,
        );

        // get all equipment assignments for the day .
        const assignedEquipmentToday = equipment
            .filter(
                (x) => x.id
                    !== (assignment ?? {})
                        .id
                    && /* (don't include the current assignment) */ !!x.assignment,
            )
            .map((e) => e.assignment);

        // filter the day's equipment assignments by the required equipment type, so the user can assign the required dependency.
        const options = assignedEquipmentToday.filter((x) => requiredEquipmentTypeIds.includes(x.equipmentTypeId));

        // display the dropdown only when theres something to show.
        return (
            <div className="d-flex flex-column">
                <Input
                    type="select"
                    placeholder="Assign Equipment"
                    className={cls({
                        'text-success': !!equipmentAssignedId,
                        'text-muted': !equipmentAssignedId,
                    })}
                    style={{ backgroundColor: !dispatchEditPermission ? 'transparent' : undefined }}
                    value={equipmentAssignedId ?? ''}
                    onChange={async (event) => {
                        if (!event.target.value) {
                            event.stopPropagation();
                            return false;
                        }

                        const resourceId = parseInt(event.target.value, 10);

                        await handleEquipmentAssignmentChange.mutateAsync(resourceId);
                    }}
                    disabled={!dispatchEditPermission || !canEdit}
                >
                    <option
                        className="text-muted"
                        value=""
                    >
                        Assign Equipment
                    </option>

                    {options.map((o) => (
                        <option
                            key={o.id}
                            value={o.id}
                        >
                            {`${o.description} (${o.equipmentType.description})`}
                        </option>
                    ))}
                </Input>

                <span
                    className={`assignment-status-label text-right ${getStatusName(assignment)} mr-2`}
                >
                    {assignment.confirmationStatusName}
                </span>
            </div>
        );
    }

    return null;
};